import React, { useEffect } from 'react';
import {
	Box,
	ClickAwayListener,
	Fade,
	Popper,
	Typography,
} from '@mui/material';
import styled from '@emotion/styled';
import { FlexCol, FlexRow } from 'components/Flex';
import { useAppSelector } from 'hooks';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { SMMPool, sMMPools } from 'data';
import PopupState, { bindPopper, bindToggle } from 'material-ui-popup-state';

const Item = styled(FlexRow)<{ isDropdown?: boolean; isActive?: boolean }>`
	width: 100%;
	border-radius: 7px;
	overflow: hidden;
	align-items: center;
	padding: 1px;
	border: ${({ isDropdown }) =>
		isDropdown ? '1px solid #235ee1' : '0px solid #235ee1'};
	margin: ${({ isDropdown }) => (isDropdown ? 0 : '0px 0px 10px 0px')};

	@media (max-width: 1200px) {
		height: 100%;
	}

	:hover {
		cursor: pointer;
		border: ${({ isDropdown }) =>
			isDropdown ? '2px solid #235ee1' : '0px solid #235ee1'};
		padding: ${({ isDropdown }) => (isDropdown ? 0 : '1px')};
		background-color: #00001f;
	}
`;

const ItemText = styled(FlexCol)`
	flex: 1;
	padding-left: 10px;
	justify-content: center;
	align-items: flex-start;
`;

const Image = styled.img`
	width: 60px;
	height: 60px;
	object-fit: contain;
`;

const DownKey = styled(KeyboardArrowDownIcon)`
	color: #ffffff;
`;

const Option = styled(FlexCol)<{ width: number }>`
	flex: 1;
	width: ${({ width }) => width}px;
	padding: 5px 5px 0 5px;
	border-radius: 5px;
	margin: 5px 10px;
	background: linear-gradient(333deg, #1a1a33 44.85%, #b8cdf5 121.45%);

	@media (min-width: 550px) {
		display: none;
	}
`;

type PoolDropdownProps = {
	onPoolSelect: (poolId: string) => void;
};

export const PoolDropdown: React.ComponentType<PoolDropdownProps> = ({
	onPoolSelect,
}) => {
	const [width, setWidth] = React.useState(300);
	const activePoolState = useAppSelector((state) => state.activePool);

	useEffect(() => {
		setWidth(window.innerWidth - 20);
		window.addEventListener('resize', () => {
			setWidth(window.innerWidth - 20);
		});

		return () => {
			window.removeEventListener('resize', () => {});
		};
	}, []);

	const RenderPoolMenu = ({
		pool,
		isDownButton,
		isActive,
		...props
	}: {
		pool?: SMMPool;
		isDownButton?: boolean;
		isActive?: boolean;
	}) => {
		if (!pool) return <></>;

		return (
			<Item isDropdown={isDownButton} {...props}>
				<Image
					src={
						isActive
							? pool?.illustration.icon
							: pool?.illustration.whiteIcon
					}
					style={{
						background: isActive
							? pool?.backgroundColor
							: 'transparent',
					}}
				/>
				<FlexRow
					justify={'space-between'}
					align={'center'}
					style={{ flex: 1, paddingRight: 10 }}
				>
					<ItemText>
						<Typography variant={'h6'} fontWeight={500}>
							{pool?.name}
						</Typography>
						<Box height={8} />
						<FlexRow>
							{pool.vaults.map((vault) => (
								<img
									width={20}
									height={20}
									key={vault.alt}
									alt={vault.alt}
									src={
										isActive ? vault.icon : vault.whiteIcon
									}
									style={{ marginRight: 8 }}
								/>
							))}
						</FlexRow>
					</ItemText>
					{isDownButton && <DownKey />}
				</FlexRow>
			</Item>
		);
	};

	return (
		<Box>
			<PopupState
				variant="popper"
				popupId="demo-popup-popper"
				disableAutoFocus={true}
			>
				{(popupState) => (
					<ClickAwayListener onClickAway={() => popupState.close()}>
						<div>
							<div {...bindToggle(popupState)}>
								<FlexRow>
									<RenderPoolMenu
										pool={activePoolState.pool}
										isDownButton={true}
									/>
								</FlexRow>
							</div>
							<Popper
								{...bindPopper(popupState)}
								transition
								placement="bottom-end"
							>
								{({ TransitionProps }) => (
									<>
										<Fade
											{...TransitionProps}
											timeout={350}
										>
											<Option width={width}>
												{sMMPools
													.filter((f) => !f.disabled)
													.map((smm) => (
														<div
															key={smm.id}
															onClick={() => {
																onPoolSelect(
																	smm.id,
																);
																popupState.close();
															}}
															style={{
																width: '100%',
															}}
														>
															<RenderPoolMenu
																pool={smm}
																isActive={
																	smm.id ===
																	activePoolState
																		.pool
																		?.id
																}
																{...smm}
															/>
														</div>
													))}
											</Option>
										</Fade>
									</>
								)}
							</Popper>
						</div>
					</ClickAwayListener>
				)}
			</PopupState>
		</Box>
	);
};
